import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface Campaign {
  id: number;
  name: string;
}

interface Contact {
  id: number;
  phoneNumber: string;
  fieldData: string;
  createdAt: string;
}

const ContactsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [phoneField, setPhoneField] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [targetCampaign, setTargetCampaign] = useState<number | null>(null);
  const [transferring, setTransferring] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCampaign) {
      fetchContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaign]);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API_BASE}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]); // Set empty array on error
    }
  };

  const fetchContacts = async () => {
    if (!selectedCampaign) return;
    try {
      const response = await axios.get(`${API_BASE}/contacts/${selectedCampaign}`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]); // Set empty array on error
    }
  };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !selectedCampaign || !phoneField) {
      alert('Please select a campaign, CSV file, and specify the phone field name');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('campaignId', selectedCampaign.toString());
    formData.append('phoneField', phoneField);

    try {
      const response = await axios.post(`${API_BASE}/contacts/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert(`Successfully imported ${response.data.imported} contacts`);
      setFile(null);
      setPhoneField('');
      fetchContacts();
    } catch (error) {
      console.error('Error uploading contacts:', error);
      alert('Error uploading contacts');
    } finally {
      setUploading(false);
    }
  };

  const exportContacts = async () => {
    if (!selectedCampaign) return;
    try {
      const response = await axios.get(`${API_BASE}/contacts/export/${selectedCampaign}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts-campaign-${selectedCampaign}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting contacts:', error);
      alert('Error exporting contacts');
    }
  };

  const transferContacts = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCampaign || !targetCampaign) return;

    setTransferring(true);
    try {
      const response = await axios.post(`${API_BASE}/contacts/transfer`, {
        fromCampaignId: selectedCampaign,
        toCampaignId: targetCampaign
      });
      alert(`Successfully transferred ${response.data.transferred} contacts`);
      setShowTransfer(false);
      setTargetCampaign(null);
    } catch (error) {
      console.error('Error transferring contacts:', error);
      alert('Error transferring contacts');
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">Contacts Management</h1>

      <div className="card">
        <h3>Select Campaign</h3>
        <div className="form-group">
          <label htmlFor="campaign">Campaign</label>
          <select
            id="campaign"
            value={selectedCampaign || ''}
            onChange={(e) => setSelectedCampaign(Number(e.target.value) || null)}
          >
            <option value="">Select a campaign...</option>
            {campaigns.map(campaign => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedCampaign && (
        <>
          <div className="card">
            <h3>Import Contacts from CSV</h3>
            <form onSubmit={handleFileUpload}>
              <div className="form-group">
                <label htmlFor="phoneField">Phone Number Column Name</label>
                <input
                  type="text"
                  id="phoneField"
                  value={phoneField}
                  onChange={(e) => setPhoneField(e.target.value)}
                  placeholder="e.g., Phone, Mobile, PhoneNumber"
                  required
                />
                <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                  Enter the exact column name that contains phone numbers
                </small>
              </div>
              
              <div className="form-group">
                <label htmlFor="csvFile">CSV File</label>
                <input
                  type="file"
                  id="csvFile"
                  accept=".csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  required
                />
                <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                  Upload a CSV file with contact information. All columns will be available as template fields.
                </small>
              </div>

              <button type="submit" className="btn" disabled={uploading}>
                {uploading ? 'Uploading...' : 'Import Contacts'}
              </button>
            </form>
          </div>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Contacts ({contacts.length})</h3>
              {contacts.length > 0 && (
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button className="btn btn-secondary" onClick={exportContacts}>
                    Export to CSV
                  </button>
                  <button className="btn" onClick={() => setShowTransfer(!showTransfer)}>
                    {showTransfer ? 'Cancel Transfer' : 'Transfer to Campaign'}
                  </button>
                </div>
              )}
            </div>

            {showTransfer && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '4px' }}>
                <h4>Transfer Contacts to Another Campaign</h4>
                <form onSubmit={transferContacts}>
                  <div className="form-group">
                    <label htmlFor="targetCampaign">Target Campaign</label>
                    <select
                      id="targetCampaign"
                      value={targetCampaign || ''}
                      onChange={(e) => setTargetCampaign(Number(e.target.value) || null)}
                      required
                    >
                      <option value="">Select target campaign...</option>
                      {campaigns
                        .filter(c => c.id !== selectedCampaign)
                        .map(campaign => (
                          <option key={campaign.id} value={campaign.id}>
                            {campaign.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button type="submit" className="btn" disabled={transferring || !targetCampaign}>
                      {transferring ? 'Transferring...' : `Transfer All ${contacts.length} Contacts`}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowTransfer(false)}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {contacts.length === 0 ? (
              <p>No contacts imported yet. Upload a CSV file to get started.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Phone Number</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Data Preview</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Imported</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map(contact => {
                      const fieldData = JSON.parse(contact.fieldData || '{}');
                      const previewFields = Object.entries(fieldData).slice(0, 3);
                      
                      return (
                        <tr key={contact.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ padding: '0.75rem' }}>{contact.phoneNumber}</td>
                          <td style={{ padding: '0.75rem' }}>
                            <div style={{ fontSize: '0.875rem' }}>
                              {previewFields.map(([key, value]) => (
                                <div key={key}>
                                  <strong>{key}:</strong> {String(value)}
                                </div>
                              ))}
                              {Object.keys(fieldData).length > 3 && (
                                <div style={{ color: '#666' }}>
                                  +{Object.keys(fieldData).length - 3} more fields
                                </div>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#666' }}>
                            {new Date(contact.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ContactsPage;
